/* global edacpFullSiteScanApp */
import { info, debug, debounce } from './helpers';
import { showNotice } from '../common/helpers';
import { scanFailTimer } from './modules/scanFailTimer';
import { __, sprintf } from '@wordpress/i18n';

const API_URL = edacpFullSiteScanApp.edacpApiUrl;
const WP_REST_NONCE = edacpFullSiteScanApp.restNonce;
const EDAC_API_URL = edacpFullSiteScanApp.edacApiUrl;
const EDAC_BASE_URL = edacpFullSiteScanApp.edacBaseUrl;

let DEBOUNCE = edacpFullSiteScanApp.scanSpeed;
if ( DEBOUNCE < 250 ) {
	DEBOUNCE = 250;
}
debug( sprintf( __( 'DEBOUNCE: %d', 'accessibility-checker-pro' ), DEBOUNCE ) );

const FORCE_RELOAD_COUNT = 100;

let scanCount = 0;

const postData = async ( url = '', data = {} ) => {
	try {
		const res = await fetch( url, {
			headers: {
				'X-WP-Nonce': WP_REST_NONCE,
				'Content-Type': 'application/json',
			},
			method: 'POST',
			body: JSON.stringify( data ),
		} );
		const result = await res.json();
		return result;
	} catch ( error ) {
		info( sprintf( __( 'Error during POST request to %s', 'accessibility-checker-pro' ), url ) );
		debug( error );
		return {};
	}
};

const getData = async ( url = '' ) => {
	try {
		const res = await fetch( url, {
			headers: {
				'X-WP-Nonce': WP_REST_NONCE,
				'Content-Type': 'application/json',
			},
			method: 'GET',
		} );
		const result = await res.json();
		return result;
	} catch ( error ) {
		info( sprintf( __( 'Error during GET request to %s', 'accessibility-checker-pro' ), url ) );
		debug( error );
		return {};
	}
};

const getHttpStatusCode = async ( url = '' ) => {
	try {
		const response = await fetch( url, {
			method: 'GET',
			redirect: 'error',
		} );

		return response.status;
	} catch ( error ) {
		info( sprintf( __( 'Error during GET request to %s', 'accessibility-checker-pro' ), url ) );
		debug( error );
		throw error;
	}
};

const saveScanResults = ( postId, violations, isSkipped = false, isFailure = false, densityMetrics = [] ) => {
	debug( sprintf( __( 'Saving: %s: started', 'accessibility-checker-pro' ), postId ) );

	postData( EDAC_API_URL + '/post-scan-results/' + postId, {
		violations,
		isSkipped,
		isFailure,
		densityMetrics,
	} )
		.then( ( data ) => {
			if ( data.success ) {
				info( sprintf( __( 'Saved: %s', 'accessibility-checker-pro' ), postId ) );

				// Small delay to give the browser a moment to settle.
				setTimeout( function() {
					getNextPage();
				}, 50 );
			} else {
				info( sprintf( __( 'Saving: %s: failed.', 'accessibility-checker-pro' ), postId ) );

				showNotice( {
					msg: __( 'Whoops! It looks like there was a problem saving a page status. Please reload the page to try again.', 'accessibility-checker-pro' ),
					type: 'error',
				} );
			}
		} )
		.catch( ( e ) => {
			info( sprintf( __( 'Saving: %s: error', 'accessibility-checker-pro' ), postId ) );
			debug( e );

			showNotice( {
				msg: __( 'Whoops! It looks like there was a problem saving a page status. Please reload the page to try again.', 'accessibility-checker-pro' ),
				type: 'error',
			} );
		} );
};

const saveScanSkip = ( postId, isFailure = false ) => {
	info( sprintf( __( 'Skipping: %s.', 'accessibility-checker-pro' ), postId ) );

	const violations = [];
	const isSkipped = true;

	saveScanResults( postId, violations, isSkipped, isFailure );
};

const scanPost = ( postID, previewUrl ) => {
	const injectIframe = ( previewFrameUrl, iFramePostID ) => {
		scanFailTimer.start( iFramePostID );

		// Create an iframe offscreen to load the preview of the page.

		// Gen unique id for this iframe
		const timestamp = new Date().getTime();
		const randomNumber = Math.floor( Math.random() * 1000 );
		const uniqueId = 'iframe' + '_' + timestamp + '_' + randomNumber;

		// inject the iframe
		const iframe = document.createElement( 'iframe' );
		iframe.setAttribute( 'id', uniqueId );
		iframe.setAttribute( 'src', previewFrameUrl );
		iframe.style.width = window.screen.width + 'px';
		iframe.style.height = window.screen.height + 'px';
		iframe.style.position = 'absolute';
		iframe.style.left = '-' + window.screen.width + 'px';
		iframe.setAttribute( 'data-timestamp', Date.now().toString() ); // Set initial timestamp

		const loadPageScanner = () => {
			debug( 'iframe loaded: ' + iFramePostID );

			// Access the contentDocument of the iframe.
			const iframeDocument =
				iframe.contentDocument || iframe.contentWindow.document;

			// Pass the iFramePostID and iframe id into the document so we can reference them from the document.
			const body = iframeDocument.querySelector( 'body' );
			body.setAttribute( 'data-iframe-id', uniqueId );
			body.setAttribute( 'data-iframe-event-name', 'edacp_scan_complete' );
			body.setAttribute( 'data-iframe-post-id', iFramePostID );

			// Inject code into iframeDocument to catch errors and disable console output.
			if ( iframeDocument ) {
				const errScriptElement = iframeDocument.createElement( 'script' );
				errScriptElement.text = `
				console.log = function(){};
				console.error = function(){};
				console.debug = function(){};
				console.info = function(){};
				window.onerror = function(){ return true; };
				`;
				iframeDocument.head.appendChild( errScriptElement );

				if ( window?.edacpFullSiteScanApp?.maxAltLength ) {
					// if the frame doesn't have window.scanOptions then create is as an object.
					if ( ! iframeDocument.defaultView.scanOptions ) {
						iframeDocument.defaultView.scanOptions = {};
					}

					// set the maxAlthLength for the scanOptions.
					iframeDocument.defaultView.scanOptions = {
						maxAltLength: window.edacpFullSiteScanApp.maxAltLength,
					};
				}

				// inject the scanner app.
				const scannerScriptElement =
					iframeDocument.createElement( 'script' );
				scannerScriptElement.src =
					EDAC_BASE_URL +
					'/build/pageScanner.bundle.js?uid=' +
					uniqueId;
				iframeDocument.head.appendChild( scannerScriptElement );
			}
		};

		document.body.append( iframe );

		// Wait for the preview to load & inject the pageScanner script.
		iframe.addEventListener( 'load', loadPageScanner );

		iframe.addEventListener( 'error', function() {
			debug( 'ERROR loading iframe:' + iFramePostID );
			scanFailTimer.stop();

			iframe.src = 'about:blank'; // Clear the source
			iframe.remove();
			saveScanSkip( iFramePostID, true );
		} );
	};

	injectIframe( previewUrl, postID );
};

const skipPost = ( postID ) => {
	// touch the post's last js scan date so we don't grab it again.
	saveScanSkip( postID );
};

const _getNextPage = () => {
	if ( typeof edacpFullSiteScanApp === 'undefined' ||
		typeof edacpFullSiteScanApp.scanStats === 'undefined' ||
		typeof edacpFullSiteScanApp.scanStats.js === 'undefined' ||
		typeof edacpFullSiteScanApp.scanStats.js.state === 'undefined'
	) {
		setTimeout( function() {
			getNextPage();
		}, 500 );
		return;
	}

	if (
		edacpFullSiteScanApp.scanStats.js.state !== 'js-scan-running'
	) {
		setTimeout( function() {
			getNextPage();
		}, 500 );

		return;
	}

	if (
		edacpFullSiteScanApp.scanStats.js.state === 'js-scan-running' &&
		edacpFullSiteScanApp.scanStats.js.pending === 0
	) {
		setTimeout( function() {
			getNextPage();
		}, 500 );

		return;
	}

	// Poll to see if there are any scans pending.
	getData( API_URL + '/scheduled-scan-url' )
		.then( ( data ) => {
			if ( data.code !== 'rest_no_route' ) {
				if ( data.data !== undefined ) {
					if ( typeof ( edacpFullSiteScanApp ) === 'undefined' ) {
						edacpFullSiteScanApp = {};
					}

					edacpFullSiteScanApp.scanStats = data.data.scanStats;

					// Only run scan on the js scan pass.
					if ( edacpFullSiteScanApp.scanStats.js.state !== 'js-scan-running' ) {
						return;
					}

					if ( data.data.scanUrl !== undefined ) {
						const liveUrl = data.data.liveUrl;
						const previewUrl = data.data.scanUrl;
						const postID = data.data.postId;

						scanCount++;
						if ( scanCount >= FORCE_RELOAD_COUNT ) {
							window.location.reload( true );
						}

						getHttpStatusCode( liveUrl )
							.then( ( status ) => {
								info( sprintf( __( 'Check post: %s', 'accessibility-checker-pro' ), postID ) );
								info( liveUrl );

								if ( status === 200 ) {
									scanPost( postID, previewUrl );
								} else {
									// The post response was not a redirect or an error, but was not 200, so skip scanning.
									debug(
										'getHttpStatusCode returned: ' + status
									);

									skipPost( postID );
								}
							} )
							.catch( ( error ) => {
								// The post returns a redirect, or a 4XX (client) / 5XX (server) error, so skip scanning.

								info(
									sprintf(
										__( '*** Skipping post %s:REDIR OR ERROR', 'accessibility-checker-pro' ),
										postID
									)
								);
								debug( error );

								saveScanSkip( postID, true );
							} );
					}
				}
			} else {
				debug( 'rest route scheduled-scan-url is not setup.' );

				showNotice( {
					msg: __( 'Whoops! It looks like there was a problem connecting to the Rest Api. Please refresh the page to try again.', 'accessibility-checker-pro' ),
					type: 'error',
				} );
			}
		} )
		.catch( ( e ) => {
			debug( 'Getting new scheduled-scan-url: error' );
			debug( e );

			showNotice( {
				msg: __( 'Whoops! It looks like there was a problem connecting to the Rest Api. Please refresh the page to try again.', 'accessibility-checker-pro' ),
				type: 'error',
			} );
		} );
};
const getNextPage = debounce( _getNextPage, DEBOUNCE );

export const init = () => {
	const handleScanComplete = ( event ) => {
		scanFailTimer.stop();
		const postId = event.detail.postId;
		const violations = event.detail.violations;

		const iframeId = event.detail.iframeId;

		const scanFailed = event.detail.failed;

		if ( event?.detail?.errorMsgs?.length > 0 ) {
			debug( '**** Scan complete, ERROR.' );
			debug( event.detail.errorMsgs );
		}

		debug( 'Scan complete, flagged issues:' );
		debug( violations );

		const iframe = document.getElementById( iframeId );
		if ( iframe ) {
			debug( 'Cleaning up iframe: ' + iframeId );
			iframe.src = 'about:blank'; // Clear the source
			// remove the iframe.
			iframe.remove();
		}
		if ( ! scanFailed ) {
			saveScanResults( postId, violations, false, false, event?.detail?.densityMetrics );
		} else {
			saveScanSkip( postId, true );
		}
	};

	getNextPage();

	// Listen for completed scans.
	// eslint-disable-next-line no-undef
	top.addEventListener( 'edacp_scan_complete', handleScanComplete );

	// Cleanup.
	window.addEventListener( 'beforeunload', function() {
		// eslint-disable-next-line no-undef
		top.removeEventListener( 'edacp_scan_complete', handleScanComplete );

		const iframes = document.querySelectorAll( 'iframe' );
		iframes.forEach( ( iframe ) => {
			debug( 'Force cleaning up iframe: ' + iframe.id );
			iframe.src = 'about:blank';
			iframe.remove();
		} );
	} );
};

